import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { MessageCircle, X, Send, Bot, Loader2 } from "lucide-react";

/**
 * Floating AI Doubt Bot widget — bottom-right, expands to a chat panel.
 * Calls /api/doubt-bot/chat with session_id persisted per-user.
 */
export default function DoubtBotWidget({ courseId }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!open || messages.length > 0) return;
    api.get("/doubt-bot/history").then(({ data }) => {
      setSessionId(data.session_id);
      const msgs = [];
      (data.messages || []).forEach((m) => {
        msgs.push({ role: "user", text: m.user_message });
        msgs.push({ role: "bot", text: m.bot_reply });
      });
      setMessages(msgs);
    }).catch(() => {});
  }, [open, messages.length]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/doubt-bot/chat", {
        message: text,
        session_id: sessionId,
        course_id: courseId,
      });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "bot", text: data.reply }]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bot reply failed");
      setMessages((m) => [...m, { role: "bot", text: "Sorry — abhi reply nahi de paya. Phir try karein." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* FAB */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-fuchsia-500 to-purple-600 shadow-lg shadow-fuchsia-500/30 flex items-center justify-center hover:scale-105 transition-transform"
        data-testid="doubt-bot-fab"
        aria-label="Doubt Bot"
      >
        {open ? <X className="w-6 h-6 text-white" /> : <MessageCircle className="w-6 h-6 text-white" />}
      </button>

      {/* Panel */}
      {open && (
        <div className="fixed bottom-24 right-6 z-50 w-[92vw] sm:w-[400px] h-[70vh] sm:h-[560px] glass-strong rounded-2xl flex flex-col overflow-hidden border border-fuchsia-400/20 shadow-2xl shadow-purple-900/40" data-testid="doubt-bot-panel">
          <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10 bg-gradient-to-r from-fuchsia-500/10 to-purple-500/10">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-fuchsia-500 to-purple-600 flex items-center justify-center">
              <Bot className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-semibold">CWK Doubt Bot</div>
              <div className="text-[10px] text-[var(--text-mute)]">Digital Marketing — Hinglish</div>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 text-sm">
            {messages.length === 0 && (
              <div className="text-center text-[var(--text-mute)] py-10">
                <Bot className="w-10 h-10 mx-auto opacity-50 mb-2" />
                <div className="text-sm">Koi bhi DM question pucho — SEO, Meta Ads, Funnels, Analytics — sab.</div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-fuchsia-500/20 text-fuchsia-100 rounded-br-md" : "bg-white/5 text-[var(--text)] rounded-bl-md"}`}>
                  {m.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white/5 px-3 py-2 rounded-2xl inline-flex items-center gap-2 text-xs text-[var(--text-mute)]">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Thinking…
                </div>
              </div>
            )}
          </div>

          <div className="px-3 py-3 border-t border-white/10 flex items-center gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Apna doubt likho…"
              className="flex-1 bg-[var(--bg-elev-1)] border border-white/10 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-fuchsia-400/40"
              data-testid="doubt-bot-input"
              disabled={loading}
            />
            <button
              onClick={send}
              disabled={loading || !input.trim()}
              className="w-10 h-10 rounded-xl bg-gradient-to-br from-fuchsia-500 to-purple-600 flex items-center justify-center disabled:opacity-40 shrink-0"
              data-testid="doubt-bot-send"
            >
              <Send className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
